import React, { useState } from "react";
// UI Framework
import { Menu, MenuItem, Tooltip } from "@mui/material";
// Common Components
import Button from "src/Components/Button";
import DatePicker from "src/Components/DatePicker";
import FullScreenLoading from "src/Components/Loading/FullScreenLoading";
import { TimePicker, Modal } from "src/Components";
import { WrappedTimeSeriesChart } from "src/Components/Timeseries/WrappedTimeSeriesChart";
import Cell from "src/Tables/TableTemplate//Cell";
// Requests
import { instrumentCalculatedNormalizedBubbleGraph } from "src/Requests/goldArbitrage";
// Utilities
import { packetErrorHandler } from "src/Utils/requestUtils";
import moment from "moment";
// Types
import { Graph_Dto } from "src/Interfaces/orderbookGraphInterfaces";
import { IInstrumentBubbleGraph } from "src/Interfaces/goldArbitrage";

const NormalizedCalculatedBubbleGraphUI = ({
  headerName,
  row,
  translateHeaderName,
}: any) => {
  // local store
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [loading, setLoading] = React.useState(false);
  const [graphData, setGraphData] = React.useState<Graph_Dto[]>([]);
  const [formValues, setFormValues] = React.useState<IInstrumentBubbleGraph>({
    date: moment().format("YYYY-MM-DD"),
    fromTime: "11:00:00",
    toTime: "18:00:00",
    instrumentId: row.instrumentId,
  });

  const [timeISOValues, setTimeISOValues] = React.useState<{
    fromTime: string | null;
    toTime: string | null;
  }>({
    fromTime: getFromTimeISODefaultValue(),
    toTime: getToTimeISODefaultValue(),
  });
  React.useEffect(() => {
    if (!isModalOpen) return;
    onSubmit();
  }, [isModalOpen]);

  function getFromTimeISODefaultValue() {
    const defaultFromTime = new Date();
    defaultFromTime.setHours(11, 0, 0, 0); // Set to 11:00 AM today

    return defaultFromTime.toISOString();
  }
  function getToTimeISODefaultValue() {
    const defaultToTime = new Date();
    defaultToTime.setHours(18, 0, 0, 0); // Set to 18:00 PM today
    return defaultToTime.toISOString();
  }

  const properFunction = () => {
    const data: IInstrumentBubbleGraph = {
      fromTime: formValues.fromTime,
      toTime: formValues.toTime,
      instrumentId: row.instrumentId,
    };
    return instrumentCalculatedNormalizedBubbleGraph({
      ...data,
      date: formValues.date,
    });
  };

  const changeHandler = (name: string, value: any) => {
    setFormValues({ ...formValues, [name]: value });
  };

  const timePickerChangeHandler = (
    name: string,
    value: {
      timeString: string;
      ISOString: string | null;
    },
  ) => {
    setTimeISOValues({ ...timeISOValues, [name]: value.ISOString });
    setFormValues({ ...formValues, [name]: value.timeString });
  };

  const onSubmit = () => {
    setLoading(true);
    properFunction()
      .then((resp: Graph_Dto | Graph_Dto[]) => {
        setGraphData(Array.isArray(resp) ? resp : resp ? [resp] : []);
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };

  const handleCloseMenu = () => {
    setIsMenuOpen(false);
    setAnchorEl(null);
    setIsTooltipOpen(false);
  };

  const rightClickHandler = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsMenuOpen(true);
    setAnchorEl(e.currentTarget);
  };

  const getChartHandler = () => {
    setIsModalOpen(true);
  };

  const ModalUI = () => {
    return (
      <>
        <div className="h-full w-full min-h-0 flex flex-row items-stretch gap-5 px-4">
          {loading ? <FullScreenLoading /> : <></>}
          <div className="flex flex-col justify-start items-start gap-3">
            <DatePicker
              label="تاریخ"
              name="date"
              onChange={changeHandler}
              value={formValues.date}
            />
            <TimePicker
              name="fromTime"
              value={timeISOValues.fromTime}
              onChange={timePickerChangeHandler}
              placeholder="از ساعت"
            />
            <TimePicker
              name="toTime"
              value={timeISOValues.toTime}
              onChange={timePickerChangeHandler}
              placeholder="تا ساعت"
            />
            <Button text="جستجو" onClick={onSubmit} variant="contained" />
          </div>
          <div className="h-full w-full mx-auto">
            <WrappedTimeSeriesChart
              dataset={graphData}
              YValueType="priceWithDecimal"
            className="h-full w-full" />
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      {isModalOpen ? (
        <Modal
          onClose={() => {
            handleCloseMenu();
            setIsModalOpen(false);
          }}
          fullScreen
          title={[translateHeaderName || headerName, row.name || row.rowName]
            .filter(Boolean)
            .join(" — ")}
          UI={ModalUI()}
          modalBoxClassName="!z-[9999]"
        />
      ) : (
        <></>
      )}
      <Tooltip
        title="نمودار"
        className="!w-full !relative"
        open={isTooltipOpen}
        onMouseEnter={() => setIsTooltipOpen(true)}
        onMouseLeave={() => setIsTooltipOpen(false)}
      >
        <div onContextMenu={rightClickHandler} className="ltr">
          <Cell clonedRow={row} headerName={headerName} type="Price" />
          {isMenuOpen ? (
            <Menu
              className="!z-[9998]"
              anchorEl={anchorEl}
              open={isMenuOpen}
              onClose={handleCloseMenu}
              MenuListProps={{
                "aria-labelledby": "basic-button",
              }}
            >
              <MenuItem onClick={getChartHandler}>نمودار</MenuItem>
            </Menu>
          ) : (
            <></>
          )}
        </div>
      </Tooltip>
    </>
  );
};

export default NormalizedCalculatedBubbleGraphUI;
